import { ArrowUpRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Reveal } from "./Reveal";

// Thin red rule that draws into place — part of the THG motion language
// derived from the official identity animation.
const DrawLine = () => {
    const reduce = useReducedMotion();
    return (
        <motion.div
            aria-hidden="true"
            className="mt-4 h-px w-16 bg-thg-red origin-left"
            initial={reduce ? false : { scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.25 }}
        />
    );
};

export const StatusBadge = ({ children, testId }) => (
    <span
        data-testid={testId}
        className="inline-block text-[10px] tracking-[0.18em] uppercase font-semibold border border-thg-border text-thg-secondary px-2.5 py-1"
    >
        {children}
    </span>
);

// Placeholder media frame — replace with real product screenshots/imagery
// when supplied by the operator (items flagged assetStatus: "pending" in data/content.js).
export const PlaceholderFrame = ({ label }) => (
    <div
        className="relative aspect-[16/9] bg-[#0d0d0d] placeholder-grid border-b border-thg-border flex items-center justify-center"
        data-asset-status="pending"
    >
        <span className="font-display text-2xl md:text-3xl text-white/80 px-6 text-center">{label}</span>
        <span className="absolute bottom-3 left-4 text-[10px] tracking-[0.2em] uppercase text-thg-tertiary">
            Authentic imagery pending
        </span>
    </div>
);

export const SectionHeading = ({ index, overline, title, text, linkTo, linkLabel, linkTestId }) => (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div className="max-w-2xl">
            <Reveal>
                <p className="text-xs tracking-[0.25em] uppercase font-bold text-thg-tertiary">
                    {index && <span className="text-thg-red mr-3">{index}</span>}
                    {overline}
                </p>
            </Reveal>
            <DrawLine />
            <Reveal delay={0.1}>
                <h2 className="mt-4 font-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-tight text-white">
                    {title}
                </h2>
            </Reveal>
            {text && (
                <Reveal delay={0.2}>
                    <p className="mt-5 text-base leading-relaxed text-thg-secondary">{text}</p>
                </Reveal>
            )}
        </div>
        {linkTo && (
            <Reveal delay={0.2}>
                <a
                    href={linkTo}
                    data-testid={linkTestId}
                    className="inline-flex items-center gap-2 text-sm text-thg-secondary hover:text-white transition-colors duration-300 group shrink-0"
                >
                    {linkLabel}
                    <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
            </Reveal>
        )}
    </div>
);
